import { Tooltip, Typography } from "antd";
import { ProvidersData } from "../../../helpers/interfaces/ProvidersInterfaces";
import {
  formatMonthlyRatePercent,
  formatRatePercent,
  groupCalculationSettings,
} from "../../../helpers/functions/calculationSettingFormat";
import "./providerExpandedRow.css";

const { Text } = Typography;

const parseMonths = (groupName?: string) => {
  const months = Number((groupName ?? "").replace("T_", ""));

  return Number.isFinite(months) && months > 0 ? months : null;
};

export const ProviderExpandedRowComponent = ({ record }: { record: ProvidersData }) => {
  const terms = [...(record.monthly_terms ?? [])]
    .map((term) => ({
      months: parseMonths(term.installment_cf_group?.installment_rates_cf_group_name),
      value: Number(term.interest_value),
      key: term.installment_cf_group_id,
    }))
    .sort((a, b) => (a.months ?? 0) - (b.months ?? 0));

  const groups = groupCalculationSettings(record.calculation_settings);
  const maxRate = Math.max(...terms.map((term) => term.value), 0);

  if (terms.length === 0 && groups.length === 0) {
    return (
      <div className="pxr" data-testid={`provider-expanded-${record.provider_id}`}>
        <Text type="secondary">No rates or calculation settings configured yet.</Text>
      </div>
    );
  }

  return (
    <div className="pxr" data-testid={`provider-expanded-${record.provider_id}`}>
      {terms.length > 0 && (
        <section className="pxr-section">
          <div className="pxr-heading">
            <span>Interest rates</span>
            <span className="pxr-count">{terms.length}</span>
            <span className="pxr-note">Total interest per term</span>
          </div>
          <div className="pxr-rates">
            {terms.map((term) => {
              const monthly = formatMonthlyRatePercent(term.value, term.months);

              return (
                <Tooltip
                  key={term.key}
                  title={
                    monthly
                      ? `Total interest over ${term.months} months (stored ${term.value}). About ${formatMonthlyRatePercent(term.value, term.months, 4)} per month.`
                      : `Stored value: ${term.value}`
                  }
                >
                  <div className="pxr-rate">
                    <span className="pxr-rate-term">{term.months ? `${term.months} months` : "Term"}</span>
                    <span className="pxr-rate-main">
                      <span className="pxr-rate-value">{formatRatePercent(term.value)}</span>
                      {monthly && <span className="pxr-rate-monthly">{monthly} / mo</span>}
                    </span>
                    <span className="pxr-rate-bar">
                      <span
                        className="pxr-rate-fill"
                        style={{ width: maxRate > 0 ? `${(term.value / maxRate) * 100}%` : "0%" }}
                      />
                    </span>
                  </div>
                </Tooltip>
              );
            })}
          </div>
        </section>
      )}

      {groups.length > 0 && (
        <section className="pxr-section">
          <div className="pxr-heading">
            <span>Calculation settings</span>
            <span className="pxr-count">{record.calculation_settings?.length ?? 0}</span>
          </div>
          <div className="pxr-groups">
            {groups.map((group) => (
              <div className="pxr-group" key={group.name}>
                <div className="pxr-group-head">
                  <span className="pxr-group-title">{group.name}</span>
                  <span className="pxr-group-count">{group.items.length}</span>
                </div>
                <dl className="pxr-list">
                  {group.items.map((item) => (
                    <Tooltip key={item.id} title={item.description || item.key}>
                      <div className="pxr-row">
                        <dt>{item.label}</dt>
                        <dd>{item.display}</dd>
                      </div>
                    </Tooltip>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};
